import type { LscArchiveItem, LscMatchSummary } from '../../../lsc/types';
import { isUrlExpired } from './isUrlExpired';
import type { RttsExistingReel, RttsGateFields } from '../types';

export type NormalizeGateHints = {
  matchup?: string;
  played_at?: string;
  archive_id?: string;
  now?: Date | number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asBool(value: unknown): boolean {
  return value === true;
}

function asOptionalString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function formatMatchup(summary?: LscMatchSummary | null): string {
  const away = (summary?.away_side_label ?? '').trim();
  const home = (summary?.home_side_label ?? '').trim();
  if (away && home) return `${away} @ ${home}`;
  if (away) return away;
  if (home) return home;
  return '';
}

/**
 * Normalize `existing_reel` from a live archive payload.
 * Malformed/absent → null. Expired signed_url is stripped so the player
 * re-fetches via job status (Prompt #2 path) instead of playing a dead URL.
 */
export function normalizeExistingReel(
  raw: unknown,
  now: Date | number = Date.now()
): RttsExistingReel | null {
  const record = asRecord(raw);
  if (!record) return null;

  const jobId = String(record.job_id ?? record.id ?? '').trim();
  if (!jobId) return null;

  const status = String(record.status ?? '').trim() || 'delivered';
  const expiresAt =
    typeof record.expires_at === 'string' && record.expires_at.trim()
      ? record.expires_at.trim()
      : null;
  const signedUrl =
    typeof record.signed_url === 'string' && record.signed_url.trim()
      ? record.signed_url.trim()
      : null;

  const reel: RttsExistingReel = {
    job_id: jobId,
    status,
    signed_url: null,
    expires_at: null,
  };

  if (
    typeof record.reel_duration_sec === 'number' &&
    Number.isFinite(record.reel_duration_sec)
  ) {
    reel.reel_duration_sec = record.reel_duration_sec;
  }

  // BACKEND-DEP: optional reel-native poster_url on existing_reel later
  const posterUrl = asOptionalString(record.poster_url);
  if (posterUrl) {
    reel.poster_url = posterUrl;
  }

  // Keep job_id even when URL is expired — consume path re-fetches the job.
  if (signedUrl && expiresAt && !isUrlExpired(expiresAt, now)) {
    reel.signed_url = signedUrl;
    reel.expires_at = expiresAt;
  } else if (signedUrl && !expiresAt) {
    // Unparseable/missing expiry → do not hand a potentially stale URL to the player.
    reel.signed_url = null;
    reel.expires_at = null;
  }

  return reel;
}

/**
 * Map a live archive (or partial) object into RttsGateFields.
 * Missing gate fields fail closed (no generate, no reel).
 */
export function normalizeGateFields(
  source: unknown,
  hints: NormalizeGateHints = {}
): RttsGateFields {
  const record = asRecord(source) ?? {};
  const archiveId = String(
    hints.archive_id || record.id || record.archive_id || ''
  ).trim();

  const matchSummary = record.match_summary as LscMatchSummary | undefined;
  const matchupFromArchive = formatMatchup(matchSummary);
  const matchup =
    (hints.matchup && hints.matchup.trim()) ||
    (typeof record.matchup === 'string' && record.matchup.trim()) ||
    matchupFromArchive ||
    'League game';

  const playedAt =
    (hints.played_at && hints.played_at.trim()) ||
    (typeof record.played_at === 'string' && record.played_at.trim()) ||
    (typeof record.published_at === 'string' && record.published_at.trim()) ||
    (typeof record.vod_created_at === 'string' && record.vod_created_at.trim()) ||
    '';

  // Archive cards use stage_label as the week segment ("Week 8").
  // // BACKEND-DEP: confirm the RTTS list endpoint surfaces season, week, broadcaster_handle
  const weekLabel =
    asOptionalString(record.week_label) ||
    asOptionalString(matchSummary?.stage_label) ||
    null;
  const seasonLabel =
    asOptionalString(record.season_label) ||
    asOptionalString(matchSummary?.season_label) ||
    null;
  const edition =
    asOptionalString(record.edition) ||
    asOptionalString(matchSummary?.edition) ||
    null;
  const broadcasterHandle =
    asOptionalString(record.broadcaster_handle) ||
    asOptionalString(record.provider_broadcaster_login) ||
    null;
  const thumbnailUrl = asOptionalString(record.thumbnail_url);

  return {
    archive_id: archiveId,
    matchup,
    played_at: playedAt,
    // Fail closed: missing/undefined entitlement or generate rights → false.
    rtts_entitled: asBool(record.rtts_entitled),
    can_generate: asBool(record.can_generate),
    existing_reel: normalizeExistingReel(record.existing_reel, hints.now),
    season_label: seasonLabel,
    edition,
    week_label: weekLabel,
    broadcaster_handle: broadcasterHandle,
    thumbnail_url: thumbnailUrl,
  };
}

/** Convenience: normalize from a typed LSC archive item. */
export function gateFieldsFromArchive(
  archive: LscArchiveItem,
  hints: NormalizeGateHints = {}
): RttsGateFields {
  return normalizeGateFields(archive, {
    archive_id: archive.id,
    ...hints,
  });
}
